import React, { useEffect, useRef, useState } from 'react';
import { eventImageSrc } from '../../../utils/eventImage';
import {
  text, list, clock, markerId, isEventMarker, isoDate, dayLabel, isPastEvent,
  currentOpening, todaysHours, openDayShorts,
} from './markerUtils';
import './MarkerTable.css';

// One row per marker. Events show their date, clubs today's opening hours;
// the picture is the one the app shows (see utils/eventImage).

const Icon = ({ path, size = 13 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
    strokeLinejoin="round" width={size} height={size} aria-hidden="true">{path}</svg>
);

const icons = {
  edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></>,
  trash: <><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></>,
  pin: <><path d="M12 22s7-7.1 7-12a7 7 0 1 0-14 0c0 4.9 7 12 7 12z" /><circle cx="12" cy="10" r="2.5" /></>,
  sortAsc: <path d="M12 19V5M6 11l6-6 6 6" />,
  sortDesc: <path d="M12 5v14M6 13l6 6 6-6" />,
};

const initials = (title) => {
  const words = String(title || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

function Thumb({ marker, title, event }) {
  const src = eventImageSrc(marker?.image);
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [src]);
  if (src && !failed) {
    return <img className="mt-thumb" src={src} alt="" loading="lazy" onError={() => setFailed(true)} />;
  }
  return <span className={`mt-thumb mt-thumb--empty ${event ? 'mt-thumb--event' : 'mt-thumb--club'}`} aria-hidden="true">{initials(title)}</span>;
}

export function rowFacts(marker, now = new Date()) {
  const event = isEventMarker(marker);
  if (event) {
    const iso = isoDate(marker);
    const start = clock(marker.startTime);
    const end = clock(marker.endTime);
    const past = isPastEvent(marker, now);
    const label = dayLabel(iso, now);
    return {
      when: label || 'Kein Datum',
      whenSub: start && end ? `${start} – ${end}` : start || '',
      status: past ? 'past' : label === 'Heute' ? 'today' : 'upcoming',
      statusLabel: past ? 'Vorbei' : label === 'Heute' ? 'Heute' : 'Geplant',
    };
  }
  const open = currentOpening(marker, now);
  const today = todaysHours(marker, now);
  const days = openDayShorts(marker);
  return {
    when: today ? `Heute ${today.open} – ${today.close}` : 'Heute geschlossen',
    whenSub: days.length ? days.join(' · ') : 'Keine Öffnungszeiten',
    status: open ? 'open' : 'closed',
    statusLabel: open ? `Offen bis ${open.close}` : 'Geschlossen',
  };
}

function SortHeader({ label, column, sort, onSort, className }) {
  const active = sort.by === column;
  return (
    <div role="columnheader" className={className} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className={`mt-hcell mt-hcell--btn${active ? ' mt-hcell--active' : ''}`} onClick={() => onSort(column)}>
        {label}
        {active && <Icon path={sort.dir === 'asc' ? icons.sortAsc : icons.sortDesc} size={11} />}
      </button>
    </div>
  );
}

export default function MarkerTable({
  rows = [],
  selectedIds = new Set(),
  onToggleSelect,
  onSelectAll,
  onOpen,
  onDelete,
  sort = { by: 'date', dir: 'asc' },
  onSort,
  now = new Date(),
}) {
  const allRef = useRef(null);
  const ids = rows.map(markerId).filter((id) => id != null);
  const allSelected = ids.length > 0 && ids.every((id) => selectedIds.has(id));
  const someSelected = ids.some((id) => selectedIds.has(id));

  useEffect(() => {
    if (allRef.current) allRef.current.indeterminate = someSelected && !allSelected;
  }, [someSelected, allSelected]);

  return (
    <div className="mt" role="table" aria-label="Marker">
      <div className="mt-head" role="row">
        <div className="mt-hcell mt-cell--check" role="columnheader">
          <input ref={allRef} type="checkbox" className="mt-cb" checked={allSelected}
            onChange={() => onSelectAll?.(ids, !allSelected)} aria-label="Alle auf dieser Seite auswählen" />
        </div>
        <div className="mt-hcell" role="columnheader" aria-label="Bild" />
        <SortHeader label="Marker" column="title" sort={sort} onSort={onSort} />
        <div className="mt-hcell mt-col--type" role="columnheader">Typ</div>
        <SortHeader label="Wann" column="date" sort={sort} onSort={onSort} className="mt-col--when" />
        <div className="mt-hcell mt-col--genres" role="columnheader">Genres</div>
        <div className="mt-hcell mt-col--status" role="columnheader">Status</div>
        <div className="mt-hcell mt-col--actions" role="columnheader" aria-label="Aktionen" />
      </div>

      {rows.map((marker) => {
        const id = markerId(marker);
        const event = isEventMarker(marker);
        const title = text(marker.title) || 'Ohne Titel';
        const address = text(marker.address);
        const instagram = text(marker.instagramName).replace(/^@/, '');
        const genres = list(marker.musicGenre);
        const facts = rowFacts(marker, now);
        const selected = selectedIds.has(id);
        const open = () => onOpen?.(marker);

        return (
          <div key={id ?? title} className={`mt-row${selected ? ' mt-row--selected' : ''}${facts.status === 'past' ? ' mt-row--past' : ''}`}
            role="row" data-testid={`marker-row-${id}`}>
            <div className="mt-cell mt-cell--check" role="cell" onClick={(e) => e.stopPropagation()}>
              <input type="checkbox" className="mt-cb" checked={selected} onChange={() => onToggleSelect?.(id)}
                aria-label={`${title} auswählen`} />
            </div>

            <div className="mt-cell mt-cell--thumb" role="cell" onClick={open}>
              <Thumb marker={marker} title={title} event={event} />
            </div>

            <div className="mt-cell mt-cell--main" role="cell">
              <button type="button" className="mt-title" onClick={open}>{title}</button>
              <span className="mt-sub">
                {address ? <><Icon path={icons.pin} size={11} /> {address}</> : instagram ? `@${instagram}` : <span className="mt-missing">Keine Adresse</span>}
              </span>
            </div>

            <div className="mt-cell mt-col--type" role="cell" onClick={open}>
              <span className={`badge ${event ? 'badge-event' : 'badge-club'}`}>{event ? 'Event' : 'Club'}</span>
            </div>

            <div className="mt-cell mt-cell--when mt-col--when" role="cell" onClick={open}>
              <span className="mt-when">{facts.when}</span>
              <span className="mt-sub">{facts.whenSub}</span>
            </div>

            <div className="mt-cell mt-col--genres" role="cell" onClick={open}>
              <span className="mt-chips">
                {genres.slice(0, 2).map((g) => <span key={g} className="mt-chip">{g}</span>)}
                {genres.length > 2 && <span className="mt-chip mt-chip--more" title={genres.slice(2).join(', ')}>+{genres.length - 2}</span>}
                {genres.length === 0 && <span className="mt-missing">–</span>}
              </span>
            </div>

            <div className="mt-cell mt-col--status" role="cell" onClick={open}>
              <span className={`mt-status mt-status--${facts.status}`}><span className="mt-dot" aria-hidden="true" />{facts.statusLabel}</span>
            </div>

            <div className="mt-cell mt-cell--actions mt-col--actions" role="cell">
              <button type="button" className="mt-act" onClick={open} aria-label={`${title} bearbeiten`} title="Bearbeiten">
                <Icon path={icons.edit} />
              </button>
              <button type="button" className="mt-act mt-act--danger" onClick={() => onDelete?.(marker)} aria-label={`${title} löschen`} title="Löschen">
                <Icon path={icons.trash} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
